import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

/**
 * BANKA HESAPLARI, CODA İÇE AKTARMA ve EŞLEŞTİRME (M7b).
 *
 * ⚠ İÇE AKTARMA DEFTERE YAZMAZ. Hareketler `UNMATCHED` doğar; deftere kayıt
 * ayrı bir adımdır ve atılmış fiş geri açılmaz.
 */
export type BankTransactionStatus = 'UNMATCHED' | 'MATCHED' | 'PARTIALLY_MATCHED' | 'IGNORED';

export const PAYMENT_TARGET_TYPES = [
  'SALES_INVOICE',
  'PURCHASE_INVOICE',
  'EXPENSE',
  'VAT_PAYMENT',
  'SOCIAL_CONTRIBUTION',
  'PRIVATE',
  'OTHER',
] as const;
export type PaymentTargetType = (typeof PAYMENT_TARGET_TYPES)[number];

export const PAYMENT_TARGET_LABELS: Record<PaymentTargetType, string> = {
  SALES_INVOICE: 'Satış faturası (tahsilat)',
  PURCHASE_INVOICE: 'Alış faturası (ödeme)',
  EXPENSE: 'Gider fişi',
  VAT_PAYMENT: 'KDV ödemesi',
  SOCIAL_CONTRIBUTION: 'Sosyal prim',
  PRIVATE: 'Özel / kişisel',
  OTHER: 'Diğer',
};

/**
 * ⚠ DURUM METİNLERİ İYİMSER OLMAMALI. `UNMATCHED`, "sorun yok" değil "bu para
 * neye ait bilinmiyor" demektir; mutabakatın tamamlanmamış olduğu tam olarak
 * budur.
 */
export const BANK_STATUS_LABELS: Record<BankTransactionStatus, string> = {
  UNMATCHED: 'Eşleşmedi — neye ait belli değil',
  PARTIALLY_MATCHED: 'Kısmen eşleşti',
  MATCHED: 'Eşleşti',
  IGNORED: 'Yok sayıldı',
};

export interface BankAccount {
  id: string;
  iban: string;
  bic: string | null;
  name: string;
  currency: string;
  ledgerAccountCode: string;
  isMain: boolean;
  active: boolean;
}

export interface BankTransaction {
  id: string;
  accountId: string;
  bookingDate: string;
  valueDate: string | null;
  /** ⚠ İŞARETLİ: giriş +, çıkış −. */
  amount: string;
  counterpartyName: string | null;
  counterpartyIban: string | null;
  communication: string | null;
  /** ⚠ Yalnız mod-97 sağlaması TUTAN 12 hane. */
  structuredCommunication: string | null;
  statementNumber: string;
  statementYear: number;
  sequenceNumber: string;
  codaTransactionCode: string | null;
  status: BankTransactionStatus;
}

export interface PaymentAllocation {
  id: string;
  targetType: PaymentTargetType;
  targetId: string | null;
  amount: string;
  matchedBy: 'AUTO_STRUCTURED' | 'AUTO_AMOUNT' | 'MANUAL';
  note: string | null;
}

export function useBankAccounts() {
  return useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => api.get<BankAccount[]>('/banking/accounts'),
  });
}

export function useBankTransactions(accountId: string | null, unmatchedOnly: boolean) {
  return useQuery({
    queryKey: ['bank-transactions', accountId ?? 'all', unmatchedOnly],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (accountId !== null) qs.set('accountId', accountId);
      if (unmatchedOnly) qs.set('unmatchedOnly', 'true');
      return api.get<BankTransaction[]>(`/banking/transactions?${qs.toString()}`);
    },
  });
}

export function useBankAllocations(transactionId: string | null) {
  return useQuery({
    queryKey: ['bank-allocations', transactionId],
    enabled: transactionId !== null,
    queryFn: () =>
      api.get<PaymentAllocation[]>(`/banking/transactions/${String(transactionId)}/allocations`),
  });
}

export function useCreateBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      iban: string;
      name: string;
      ledgerAccountCode: string;
      bic?: string | null;
      isMain?: boolean;
    }) => api.post<BankAccount>('/banking/accounts', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['bank-accounts'] }),
  });
}

/**
 * ⚠ CODA HAM BAYT OLARAK GİDER, multipart DEĞİL — ek yüklemeleriyle aynı
 * gerekçe: multipart kullanıcı girdisinden gelen bir dosya adı taşır.
 */
export function useImportCoda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { accountId: string; file: File }) => {
      const bytes = await input.file.arrayBuffer();
      return api.postRaw<{
        batchId: string;
        statements: number;
        movements: number;
        skippedDetails: number;
        duplicates: number;
      }>(`/banking/accounts/${input.accountId}/coda`, bytes, 'text/plain');
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['bank-transactions'] }),
  });
}

/** ⚠ YALNIZ OGM ÜZERİNDEN — tutar benzerliğiyle DEĞİL. */
export function useAutoMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountId?: string) =>
      api.post<{ matched: number; candidates: number }>('/banking/auto-match', { accountId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['bank-transactions'] }),
  });
}

export function useAllocatePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      transactionId: string;
      targetType: PaymentTargetType;
      targetId?: string | null;
      amount: string;
      note?: string | null;
    }) =>
      api.post<PaymentAllocation>(`/banking/transactions/${input.transactionId}/allocations`, {
        targetType: input.targetType,
        targetId: input.targetId ?? null,
        amount: input.amount,
        note: input.note ?? null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bank-transactions'] });
      void qc.invalidateQueries({ queryKey: ['bank-allocations'] });
    },
  });
}

export function useIgnoreTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<BankTransaction>(`/banking/transactions/${id}/ignore`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['bank-transactions'] }),
  });
}
